import { useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { captureException, captureMessage } from '@sentry/react'
import type { ScannerHandle } from '@/lib/barcode-camera'
import type { ContractDeliveryOrder } from '@/lib/api/contract-delivery-api'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { usePickupOrder } from '@/hooks/use-sales-order-query'
import {
    applyContinuousFocus,
    createBarcodeScanner,
    reportIfScannerVideoCollapsed,
    reportScannerCameraDiagnostics,
    triggerRefocus,
} from '@/lib/barcode-camera'
import { salesOrderApi } from '@/lib/api/sales-order-api'
import { contractDeliveryApi } from '@/lib/api/contract-delivery-api'
import {
    useConfirmContractDeliveryDone,
    usePickupContractDelivery,
} from '@/hooks/use-contract-delivery-query'
import { useAuth } from '@/hooks/use-auth'
import { m } from '@/lib/paraglide/messages'
import {
    reportScannerStartError,
    scannerCameraErrorMessage,
} from '@/lib/scanner-start-error'

const UUID_RE =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const ORDER_URL_RE = /\/orders\/([a-f0-9-]+)/i
const STOCK_OUT_URL_RE = /\/stock-out-notes\/([^/?#]+)/i
// Đơn giao hợp đồng: QR nhúng URL /contract-deliveries/{id}, mã đơn prefix GHD.
const CONTRACT_URL_RE = /\/contract-deliveries\/([a-f0-9-]+)/i
export type ScanKind = 'order' | 'stock_out' | 'contract'
export type ScanResult =
    | { kind: 'order'; orderId: string }
    | { kind: 'stock_out'; code: string; orderId: string }
    | { kind: 'contract'; order: ContractDeliveryOrder }

export async function resolveScan(text: string): Promise<ScanResult | null> {
    const trimmed = text.trim()
    if (!trimmed) return null

    const stockOutUrl = trimmed.match(STOCK_OUT_URL_RE)
    if (stockOutUrl) {
        const code = decodeURIComponent(stockOutUrl[1])
        const res = await salesOrderApi.lookupByCode(code)
        const orderId = res.data?.id
        if (!orderId) return null
        return { kind: 'stock_out', code, orderId }
    }

    const contractUrl = trimmed.match(CONTRACT_URL_RE)
    if (contractUrl) {
        const res = await contractDeliveryApi.get(contractUrl[1])
        if (!res.data) return null
        return { kind: 'contract', order: res.data }
    }

    const orderUrl = trimmed.match(ORDER_URL_RE)
    if (orderUrl) return { kind: 'order', orderId: orderUrl[1] }

    if (UUID_RE.test(trimmed)) return { kind: 'order', orderId: trimmed }

    if (/^PXK/i.test(trimmed)) {
        const res = await salesOrderApi.lookupByCode(trimmed)
        const orderId = res.data?.id
        if (!orderId) return null
        return { kind: 'stock_out', code: trimmed, orderId }
    }

    if (/^GHD/i.test(trimmed)) {
        const res = await contractDeliveryApi.lookupByCode(trimmed)
        if (!res.data) return null
        return { kind: 'contract', order: res.data }
    }

    if (/^DH/i.test(trimmed)) {
        const res = await salesOrderApi.lookupByCode(trimmed)
        const orderId = res.data?.id
        if (!orderId) return null
        return { kind: 'order', orderId }
    }

    return null
}

// contractScanAction — quyết định hành động khi tài xế quét đơn hợp đồng:
// 'pickup' (đơn đã xác nhận, chưa ai nhận) → nhận đơn;
// 'confirm' (đơn đang giao của chính tài xế) → xác nhận đã giao;
// 'navigate' → chỉ mở trang chi tiết.
export function contractScanAction(
    order: ContractDeliveryOrder,
    userId: string | undefined,
): 'pickup' | 'confirm' | 'navigate' {
    if (order.status === 'confirmed' && !order.driver_assignee_id)
        return 'pickup'
    if (
        order.status === 'in_delivery' &&
        !!userId &&
        order.driver_assignee_id === userId
    )
        return 'confirm'
    return 'navigate'
}

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    mode?: 'navigate' | 'pickup'
    onComplete?: () => void
}

// Ship the lens/focus report as a searchable info event — a blurry
// fixed-focus lens throws no error, so this is the only prod signal saying
// which camera a failing device got bound to.
const captureScannerDiagnostics = (
    message: string,
    data: Record<string, unknown>,
) =>
    captureMessage(message, {
        level: 'info',
        tags: { feature: 'barcode-scanner' },
        extra: data,
    })

function getModeConfig(mode: 'navigate' | 'pickup', kind: ScanKind | null) {
    if (mode === 'pickup') {
        return {
            title: m.qr_pickup_title(),
            description: m.qr_pickup_desc(),
            submitLabel: m.qr_pickup_submit(),
            errorMsg: m.qr_pickup_error(),
        }
    }
    if (kind === 'stock_out') {
        return {
            title: m.qr_warehouse_title(),
            description: m.qr_warehouse_desc(),
            submitLabel: m.qr_warehouse_submit(),
            errorMsg: m.qr_warehouse_error(),
        }
    }
    return {
        title: m.qr_navigate_title(),
        description: m.qr_navigate_desc(),
        submitLabel: m.qr_navigate_submit(),
        errorMsg: m.qr_navigate_error(),
    }
}

export function QRScannerDialog({
    open,
    onOpenChange,
    mode = 'navigate',
    onComplete,
}: Props) {
    const [scanning, setScanning] = useState(false)
    const [manualId, setManualId] = useState('')
    const [error, setError] = useState('')
    const [pending, setPending] = useState(false)
    const [lastKind, setLastKind] = useState<ScanKind | null>(null)
    const handleRef = useRef<ScannerHandle | null>(null)
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const isStartingRef = useRef(false)
    // Bumped by every start and stop. A camera start can take seconds, and the
    // operator may close (or close and reopen) the dialog meanwhile; a start
    // whose number is no longer current was cancelled and must not touch the
    // live scanner, its error message, or Sentry when it finally settles.
    const startAttemptRef = useRef(0)
    const navigate = useNavigate()
    const pickupOrder = usePickupOrder()
    const pickupContract = usePickupContractDelivery()
    const confirmContractDone = useConfirmContractDeliveryDone()
    const { user } = useAuth()
    const config = getModeConfig(mode, lastKind)

    const stopScanner = async () => {
        startAttemptRef.current += 1
        isStartingRef.current = false
        if (handleRef.current) {
            try {
                await handleRef.current.stop()
            } catch {
                /* already stopped */
            }
            handleRef.current = null
        }
        setScanning(false)
    }

    const handleQRResult = async (rawText: string) => {
        await stopScanner()
        setError('')
        setPending(true)
        try {
            const result = await resolveScan(rawText)
            if (!result) {
                setError(config.errorMsg)
                return
            }
            setLastKind(result.kind)

            // Đơn giao hợp đồng: CHỈ ở mode pickup mới hành động (nhận đơn khi
            // đơn chờ, xác nhận đã giao khi quét lại lúc đang giao). Scanner
            // navigate (header sidebar, danh sách đơn) chỉ mở trang chi tiết —
            // không được tự thực thi thao tác của tài xế.
            if (result.kind === 'contract') {
                const action =
                    mode === 'pickup'
                        ? contractScanAction(result.order, user?.id)
                        : 'navigate'
                if (action === 'pickup') {
                    await pickupContract.mutateAsync(result.order.id)
                } else if (action === 'confirm') {
                    await confirmContractDone.mutateAsync(result.order.id)
                }
                onOpenChange(false)
                if (action === 'navigate') {
                    void navigate({
                        to: '/contract-deliveries/$id',
                        params: { id: result.order.id },
                    })
                }
                onComplete?.()
                return
            }

            if (mode === 'pickup') {
                await pickupOrder.mutateAsync({ id: result.orderId })
                onOpenChange(false)
                onComplete?.()
                return
            }

            // stock_out QR: trang /stock-out-notes/$code (legacy packing flow)
            // đã bị gỡ sau product_lots redesign. Fallback navigate sang chính
            // sales_order detail — user xác nhận giao tại đó.
            onOpenChange(false)
            void navigate({
                to: '/orders/$orderId',
                params: { orderId: result.orderId },
            })
            onComplete?.()
        } catch {
            setError(config.errorMsg)
        } finally {
            setPending(false)
        }
    }

    const startScanner = async () => {
        if (isStartingRef.current || handleRef.current) return
        const attempt = ++startAttemptRef.current
        const isCancelled = () => attempt !== startAttemptRef.current
        isStartingRef.current = true
        setError('')
        setScanning(true)
        const videoEl = videoRef.current
        if (!videoEl) {
            isStartingRef.current = false
            return
        }
        try {
            const handle = await createBarcodeScanner({
                videoEl,
                formats: 'qr-only',
                onResult: (text) => {
                    void handleQRResult(text)
                },
                onError: (err) => captureException(err),
            })
            if (isCancelled()) {
                await handle.stop()
                return
            }
            handleRef.current = handle
            const track = handleRef.current.getVideoTrack()
            if (track) {
                const focusAccepted = await applyContinuousFocus(track)
                reportScannerCameraDiagnostics(
                    track,
                    focusAccepted,
                    captureScannerDiagnostics,
                )
            }
            reportIfScannerVideoCollapsed(videoEl, captureException)
        } catch (err) {
            if (isCancelled()) return
            reportScannerStartError(err, { captureException, captureMessage })
            setScanning(false)
            setError(scannerCameraErrorMessage(err))
            handleRef.current = null
        } finally {
            if (!isCancelled()) isStartingRef.current = false
        }
    }

    const handleManualSubmit = () => {
        if (!manualId.trim()) return
        void handleQRResult(manualId.trim())
    }

    const handleTapFocus = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!scanning || !handleRef.current) return
        const track = handleRef.current.getVideoTrack()
        if (!track) return
        const rect = e.currentTarget.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return
        const clamp01 = (v: number) => Math.min(1, Math.max(0, v))
        void triggerRefocus(track, {
            x: clamp01((e.clientX - rect.left) / rect.width),
            y: clamp01((e.clientY - rect.top) / rect.height),
        })
    }

    useEffect(() => {
        if (!open) {
            void stopScanner()
            setManualId('')
            setError('')
            setLastKind(null)
        }
    }, [open])

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>{config.title}</DialogTitle>
                    <DialogDescription>{config.description}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div
                        data-testid="qr-scanner-stage"
                        className="relative aspect-square w-full overflow-hidden rounded-md bg-muted cursor-pointer"
                        onClick={handleTapFocus}
                    >
                        <video
                            ref={videoRef}
                            data-testid="qr-scanner-video"
                            className="block h-full w-full object-cover"
                            autoPlay
                            playsInline
                            muted
                        />
                    </div>

                    {!scanning ? (
                        <Button
                            className="w-full"
                            onClick={() => void startScanner()}
                        >
                            {m.qr_open_camera()}
                        </Button>
                    ) : (
                        <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => void stopScanner()}
                        >
                            {m.qr_stop_scanning()}
                        </Button>
                    )}

                    {error && (
                        <p className="text-sm text-destructive">{error}</p>
                    )}

                    <div className="relative flex items-center gap-2">
                        <div className="flex-1 border-t" />
                        <span className="text-xs text-muted-foreground">
                            {m.common_or_manual_input()}
                        </span>
                        <div className="flex-1 border-t" />
                    </div>

                    <div className="flex gap-2">
                        <Input
                            placeholder={m.qr_manual_placeholder()}
                            value={manualId}
                            onChange={(e) => setManualId(e.target.value)}
                            onKeyDown={(e) =>
                                e.key === 'Enter' && handleManualSubmit()
                            }
                        />
                        <Button
                            disabled={
                                !manualId.trim() ||
                                pending ||
                                pickupOrder.isPending
                            }
                            onClick={handleManualSubmit}
                        >
                            {config.submitLabel}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}
